// state.js — Central application state model for Peerly
// All app state lives here. UI renders from state, not from scattered DOM mutations.

import { ADJECTIVES, ANIMALS, ROOM } from './config.js';

/**
 * Application lifecycle phases.
 * The app moves through these phases in order; UI renders based on current phase.
 */
export const Phase = Object.freeze({
  IDLE: 'idle',
  PREPARING_MEDIA: 'preparing_media',
  CREATING_ROOM: 'creating_room',
  WAITING_FOR_PEER: 'waiting_for_peer',
  JOINING_ROOM: 'joining_room',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ENDED: 'ended',
  FAILED: 'failed',
});

/**
 * Participant roles in a room.
 */
export const Role = Object.freeze({
  SENDER: 'sender',
  RECEIVER: 'receiver',
});

/**
 * Generates a collision-resistant session ID.
 * Format: adjective-animal-animal-NNNN (e.g. bold-fox-deer-4821)
 * ~160k combinations from word lists × 9000 numeric suffixes ≈ 1.4 billion.
 */
export function generateSessionId() {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const a1 = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
  const a2 = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
  const digits = Math.floor(Math.random() * 9000) + 1000;
  return `${adj}-${a1}-${a2}-${digits}`;
}

/**
 * Normalizes and validates a user-provided slug.
 * Returns { valid: boolean, slug: string, error?: string }
 */
export function normalizeSlug(raw) {
  if (!raw || typeof raw !== 'string') {
    return { valid: false, slug: '', error: 'Empty slug' };
  }
  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
  if (slug.length === 0) {
    return { valid: false, slug: '', error: 'Slug is empty after normalization' };
  }
  if (slug.length < 2) {
    return { valid: false, slug, error: 'Slug must be at least 2 characters' };
  }
  if (slug.length > ROOM.MAX_SLUG_LENGTH) {
    return {
      valid: false,
      slug: slug.slice(0, ROOM.MAX_SLUG_LENGTH),
      error: `Slug too long (max ${ROOM.MAX_SLUG_LENGTH})`,
    };
  }
  if (ROOM.RESERVED_SLUGS.has(slug)) {
    return { valid: false, slug, error: `"${slug}" is a reserved name` };
  }
  return { valid: true, slug };
}

/**
 * Parse a user-provided join input that could be:
 * - A full URL: https://peerly.site/?session=bold-fox-deer-4821
 * - A partial URL: peerly.site/?session=bold-fox-deer-4821
 * - A query string: ?session=bold-fox-deer-4821
 * - A raw slug: bold-fox-deer-4821
 * Returns the raw extracted string (not yet normalized).
 */
export function parseJoinInput(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Try parsing as a full URL
  try {
    const url = new URL(trimmed);
    const session = url.searchParams.get('session');
    if (session) return session;
  } catch {
    // Not a full URL — try as relative URL with query string
    if (trimmed.includes('?')) {
      const queryPart = trimmed.split('?').slice(1).join('?');
      const params = new URLSearchParams(queryPart);
      const session = params.get('session');
      if (session) return session;
    }
  }

  // Fall back to raw string (could be a slug)
  return trimmed;
}

/**
 * Central mutable app state.
 * Subscribers are notified on state changes via onChange callbacks.
 */
const _listeners = new Set();

const _state = {
  // Lifecycle
  phase: Phase.IDLE,

  // Session identity
  roomId: '',
  role: Role.SENDER,

  // Participant info
  localNickname: 'Anonymous',
  remoteNickname: '',

  // Media state
  isVideoEnabled: true,
  isAudioEnabled: true,
  isMirrored: false,
  localStream: null,
  remoteStream: null,

  // RTC state
  peerConnection: null,
  dataChannel: null,
  iceConnectionState: '',
  dataChannelState: 'closed',

  // UI flags
  isCoolMode: false,
  isDarkMode: false,
  isFullscreen: false,
  isSplitView: false,
  isSwapped: false,
  isEmojiPickerOpen: false,
  currentEmojiPage: 0,
  currentFontSizeIndex: 1,

  // Room presence
  isRoomOwner: false,
  roomExists: false,
  roomStatus: '', // 'waiting', 'active', 'ended' (stale inferred from heartbeat age)
  heartbeatTimer: null,

  // Error
  lastError: null,

  // Signaling listener unsub handles
  roomListener: null,
  candidatesListener: null,

  // Audio visualizer
  audioContext: null,
  visualizerCancelFn: null,

  // Disconnect overlay reference
  disconnectOverlay: null,

  // Track whether we've announced join
  hasAnnouncedJoin: false,

  // Pre-call device preview
  previewStream: null,
  previewVideoDeviceId: null,
  previewAudioDeviceId: null,

  // Screen sharing
  isScreenSharing: false,
  screenStream: null,
  originalVideoTrack: null,

  // Picture-in-picture
  isPiPActive: false,

  // File transfer
  fileTransfers: [], // { id, name, size, direction, progress, status }

  // ICE restart tracking
  restartAttempts: 0,
  restartTimer: null,

  // Connection panel error mode ('default' | 'roomFull' | 'roomNotFound' | 'roomEnded')
  panelMode: 'default',
};

/**
 * Get a shallow copy of state (read-only intent).
 */
export function getState() {
  return { ..._state };
}

/**
 * Get mutable state reference (for internal module use only).
 * Prefer update() for trackable mutations.
 */
export function getMutableState() {
  return _state;
}

/**
 * Update state and notify listeners.
 * @param {Partial<typeof _state>} patch — key-value pairs to merge into state.
 */
export function update(patch) {
  const changed = {};
  for (const key of Object.keys(patch)) {
    if (_state[key] !== patch[key]) {
      changed[key] = { from: _state[key], to: patch[key] };
      _state[key] = patch[key];
    }
  }
  if (Object.keys(changed).length > 0) {
    for (const fn of _listeners) {
      try {
        fn(changed, _state);
      } catch (e) {
        console.error('[state] listener error:', e);
      }
    }
  }
}

/**
 * Subscribe to state changes.
 * @param {(changed: object, state: object) => void} fn
 * @returns {() => void} unsubscribe function
 */
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Reset state to initial values (for end-call / teardown).
 */
export function reset() {
  update({
    phase: Phase.IDLE,
    role: Role.SENDER,
    roomId: '',
    localNickname: 'Anonymous',
    remoteNickname: '',
    isVideoEnabled: true,
    isAudioEnabled: true,
    isMirrored: false,
    localStream: null,
    remoteStream: null,
    peerConnection: null,
    dataChannel: null,
    iceConnectionState: '',
    dataChannelState: 'closed',
    isFullscreen: false,
    isSplitView: false,
    isSwapped: false,
    isEmojiPickerOpen: false,
    currentEmojiPage: 0,
    isRoomOwner: false,
    roomExists: false,
    roomStatus: '',
    heartbeatTimer: null,
    lastError: null,
    roomListener: null,
    candidatesListener: null,
    audioContext: null,
    visualizerCancelFn: null,
    disconnectOverlay: null,
    hasAnnouncedJoin: false,
    isScreenSharing: false,
    screenStream: null,
    originalVideoTrack: null,
    isPiPActive: false,
    fileTransfers: [],
    previewStream: null,
    previewVideoDeviceId: null,
    previewAudioDeviceId: null,
    restartAttempts: 0,
    restartTimer: null,
    panelMode: 'default',
  });
}
